import { useEffect, useRef } from 'react';
import { postMessage } from '../helpers/utils';
import {
  AUDIO_STOPPED_REPORT_DELAY_MS,
  MESSAGE_TYPES
} from '../constants/simulationConstants';
import { useSimulationContext } from '../providers/SimulationProvider';

function useAudioMessageListener({ audioRef }) {
  const mutedRef = useRef(false);
  const unmuteTimeoutRef = useRef(null);
  const { isAiTalking } = useSimulationContext();

  useEffect(() => {
    const handleMessage = (event) => {
      let receivedData = event.data;

      if (typeof receivedData === 'string') {
        try {
          receivedData = JSON.parse(receivedData);
        } catch (err) {
          return;
        }
      }

      if (
        receivedData.type === MESSAGE_TYPES.AUDIO &&
        typeof receivedData.payload?.muted === 'boolean'
      ) {
        // Record the preference even when nothing is playing yet, so the next
        // chunk of this reply honours it (playNextInQueue reads mutedRef). It
        // only lasts until the reply ends — see the reset below.
        mutedRef.current = receivedData.payload.muted;
        if (audioRef.current)
          audioRef.current.muted = receivedData.payload.muted;

        postMessage({
          type: MESSAGE_TYPES.AUDIO,
          payload: {
            muted: mutedRef.current
          }
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [audioRef]);

  /**
   * Muting silences only the reply being spoken. Once the coach has stayed
   * quiet for the same window useAudioPlaybackReporter waits before telling the
   * parent `muted: false`, unmute here too — otherwise the parent shows the
   * control unmuted while every later reply stays silent. A shorter gap is just
   * the pause between two chunks of one reply and must keep it muted.
   */
  useEffect(() => {
    if (unmuteTimeoutRef.current) {
      clearTimeout(unmuteTimeoutRef.current);
      unmuteTimeoutRef.current = null;
    }

    if (isAiTalking) return undefined;

    unmuteTimeoutRef.current = setTimeout(() => {
      unmuteTimeoutRef.current = null;
      mutedRef.current = false;
      if (audioRef.current) audioRef.current.muted = false;
    }, AUDIO_STOPPED_REPORT_DELAY_MS);

    return () => {
      if (!unmuteTimeoutRef.current) return;
      clearTimeout(unmuteTimeoutRef.current);
      unmuteTimeoutRef.current = null;
    };
  }, [isAiTalking, audioRef]);

  return { mutedRef };
}

export default useAudioMessageListener;
